import * as React from 'react'
import ReactCrop, {
  Crop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import RadioButton from './RadioButton'
import Button from './Button'
import { formatUrl } from '../helpers/getFormattedUrl'
import styles from './ImageCrop.module.css'
import OptimizedImage from './OptimizedImage'

function centerAspectCrop (
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

interface ImageCropPage {
  imgSrc: string | undefined,
  imgHeight: number | undefined,
  imgWidth: number | undefined,
  imageName: string,
  imageUrl: string | undefined
  onOpenUrlModal: () => void,
  setCroppedImageUrl: (imageUrl: string) => void,
  setCroppedImageAspectRatio: (aspectRatio: number) => void,
}

function ImageCrop ({ imgSrc, imgHeight, imgWidth, imageName, imageUrl, onOpenUrlModal, setCroppedImageUrl, setCroppedImageAspectRatio }: ImageCropPage) {

  const imgRef = React.useRef<HTMLImageElement | null>(null)
  const [crop, setCrop] = React.useState<Crop>()
  const [aspect, setAspect] = React.useState<number | undefined>(1)
  const [selectedImageWidth, setSelectedImageWidth] = React.useState<number>()
  const [selectedImageHeight, setSelectedImageHeight] = React.useState<number>()

  function onImageLoad (e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget
      setCrop(centerAspectCrop(width, height, aspect))
      setSelectedImageWidth(imgWidth)
      setSelectedImageHeight(imgHeight)
    }
  }

  function updateToggleAspectTo (selectedAspect: number | undefined,) {
    setAspect(selectedAspect)

    if (selectedAspect && imgRef.current) {
      const { width, height } = imgRef.current
      setCrop(centerAspectCrop(width, height, selectedAspect))
    }
  }

  function onSaveCrop () {
    const uploadedImage: HTMLImageElement | null = imgRef.current
    if (uploadedImage && selectedImageWidth && selectedImageHeight) {
      if (crop) {
        const coordinateX = Math.round(selectedImageWidth * (crop.x / 100))
        const coordinateY = Math.round(selectedImageHeight * (crop.y / 100))
        const coordinateWidth = Math.round(selectedImageWidth * (crop.width / 100))
        const coordinateHeight = Math.round(selectedImageHeight * (crop.height / 100))
        const coordinateString = `$coordinate_1,x_${coordinateX},y_${coordinateY},w_${coordinateWidth},h_${coordinateHeight},c_crop`
        setCroppedImageAspectRatio(coordinateWidth / coordinateHeight)
        imageUrl && setCroppedImageUrl(formatUrl(imageName, imageUrl, coordinateString))
        onOpenUrlModal()
      }
    }
  }

  return (
    <div className={styles.imageCropContainer}>
      <div className={styles.aspect}>
        <h3>Bildformat</h3>
        <div className={styles.aspectButtons}>
          <div className={styles.aspectItem}>
            <RadioButton
              id="1_1"
              label="1:1"
              name="aspectRatio"
              value={1}
              checked={aspect === 1}
              onChange={() => updateToggleAspectTo(1)}
            />
          </div>
          <div className={styles.aspectItem}>
            <RadioButton
              id="16_9"
              label="16:9"
              name="aspectRatio"
              value={16 / 9}
              checked={aspect === 16 / 9}
              onChange={() => updateToggleAspectTo(16 / 9)}
            />
          </div>
          <div className={styles.aspectItem}>
            <RadioButton
              id="dynamic"
              label="Fri"
              name="aspectRatio"
              value={undefined}
              checked={aspect === undefined}
              onChange={() => updateToggleAspectTo(undefined)}
            />
          </div>
        </div>
      </div>
      <>
        <div className={styles.imageCrop}>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            aspect={aspect}
            style={{ display: 'block', maxWidth: '500px' }}
          >
            {imgSrc && imgRef && (
              <OptimizedImage
                className={styles.image}
                ref={imgRef}
                alt="Crop"
                src={imgSrc}
                onLoad={onImageLoad}
                aspectRatio={imgWidth && imgHeight ? imgWidth / imgHeight : 1}
                sizes='30vw'
              />
            )}
          </ReactCrop>
        </div>
        <div className={styles.cropAction}>
          <Button size='large' onClick={onSaveCrop}>Hämta URL</Button>
        </div>
      </>
    </div>
  )
}

export default ImageCrop
